import type { ScriptManager } from 'lib/scriptManager';

import { AnalyticsBase } from '../base';
import { AnalyticsID } from '../constants';
import type { AnalyticsSink } from '../types';
import { cartPayload, itemPayloadFromProduct } from './payloads';

export enum CvgMethod {
  TRACK = 'track',
  CONSENT = 'consent',
}

export enum CvgEvent {
  ADDED_TO_CART = 'Added To Cart',
  VIEWED_ITEM = 'Viewed Product',
  VIEWED_CART = 'Viewed Cart',
  PAGE_LOAD = '$page_load',
  OVERLAY_POPUP_CLICK = 'overlayPopup/click',
  SITE_TOGGLE_SKINCARE_CLICK = 'site_toggle/skincare_click',
  SITE_TOGGLE_SUPPLEMENTS_CLICK = 'site_toggle/supplements_click',
  SKINCARE_CARD_CLICK = 'skincare_card/click',
  LOGGED_IN = 'Logged In',
}

export class ConvergeAnalytics extends AnalyticsBase implements AnalyticsSink {
  id = AnalyticsID.Converge;

  scriptManager: ScriptManager;

  constructor(scriptManager: ScriptManager) {
    super();
    this.scriptManager = scriptManager;
  }

  isReady() {
    return this.scriptManager.isScriptLoaded(this.id);
  }

  events = {
    pageView() {
      window.cvg({ method: CvgMethod.TRACK, eventName: CvgEvent.PAGE_LOAD });
    },

    addToCart(items) {
      items.forEach(({ product, merchandiseId, quantity }) => {
        const payload = itemPayloadFromProduct(product, merchandiseId);

        if (!payload) return;

        window.cvg({
          method: CvgMethod.TRACK,
          eventName: CvgEvent.ADDED_TO_CART,
          properties: {
            ...payload,
            quantity,
          },
        });
      });
    },

    viewItem({ product, merchandiseId }) {
      const payload = itemPayloadFromProduct(product, merchandiseId);

      if (!payload) return;

      window.cvg({
        method: CvgMethod.TRACK,
        eventName: CvgEvent.VIEWED_ITEM,
        properties: payload,
      });
    },

    viewCart({ cart }) {
      const payload = cartPayload(cart);

      window.cvg({
        method: CvgMethod.TRACK,
        eventName: CvgEvent.VIEWED_CART,
        properties: payload,
      });
    },

    overlayPopupClick({ url }) {
      window.cvg({
        method: CvgMethod.TRACK,
        eventName: CvgEvent.OVERLAY_POPUP_CLICK,
        properties: { url },
      });
    },

    siteToggleSkincareClick() {
      window.cvg({
        method: CvgMethod.TRACK,
        eventName: CvgEvent.SITE_TOGGLE_SKINCARE_CLICK,
      });
    },

    siteToggleSupplementsClick() {
      window.cvg({
        method: CvgMethod.TRACK,
        eventName: CvgEvent.SITE_TOGGLE_SUPPLEMENTS_CLICK,
      });
    },

    skincareCardClick({ slug, url }) {
      window.cvg({
        method: CvgMethod.TRACK,
        eventName: CvgEvent.SKINCARE_CARD_CLICK,
        properties: { slug, url },
      });
    },

    cartUpsellAccepted({ type, product, variant }) {
      window.cvg({
        method: CvgMethod.TRACK,
        eventName: `cart/upsell_${type}`,
        properties: { product, variant: variant ?? null },
      });
    },

    login({ user: { email } }) {
      window.cvg({
        method: CvgMethod.TRACK,
        eventName: CvgEvent.LOGGED_IN,
        profileProperties: { $email: email },
        aliases: [`urn:email:${email}`],
      });
    },
  } satisfies AnalyticsSink['events'];
}
